/**
 * AgentToolsPanel — bundles the AI Assistant toggle, Payment Link button,
 * and Issue Tags chips that sit under the Info tab in ChatsPage.
 *
 * Iter 152.4 (Feb 2026, Yash) — extracted from ChatsPage.jsx to keep that
 * file moving toward a sensible size. Pure presentational; the parent owns
 * the lead state, AI handlers, and payment modal state.
 */
import { X, Sparkles, IndianRupee } from "lucide-react";

const AgentToolsPanel = ({
  lead,
  onAiStart,
  onAiStop,
  onPaymentLinkOpen,
  availableIssueTags,
  onToggleIssueTag,
}) => (
  <>
    {/* AI Assistant — per-chat toggle */}
    <div className="px-4 py-3 border-t border-[--border]" data-testid="start-ai-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-bold text-[--text-muted] uppercase tracking-wider">AI Assistant</p>
        {lead?.ai_active && (
          <span className="flex items-center gap-1 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full" data-testid="ai-active-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> ACTIVE
          </span>
        )}
      </div>
      {lead?.ai_active ? (
        <>
          <div className="mb-2 p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-[10px] text-emerald-800 leading-snug" data-testid="ai-active-banner">
            <b>AI is replying on this chat.</b> Every customer message will be auto-answered until you turn this off.
          </div>
          <button
            onClick={onAiStop}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-white border-2 border-red-200 text-red-600 text-[11px] font-bold hover:bg-red-50 transition-all"
            data-testid="stop-ai-btn-panel"
            title="Turn off AI for this chat — customer replies will wait for an agent"
          >
            <X size={13} /> Stop AI on this chat
          </button>
        </>
      ) : (
        <button
          onClick={onAiStart}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-violet-600 text-white text-[11px] font-bold hover:from-purple-600 hover:to-violet-700 transition-all shadow-sm"
          data-testid="start-ai-btn-panel"
          title="Turn on AI for this chat — the AI will reply now and keep replying to every customer message until you stop it"
        >
          <Sparkles size={13} /> Start AI on this chat
        </button>
      )}
      {lead?.ai_shared_payment && (
        <div className="mt-2 text-[10px] bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1 text-emerald-800" data-testid="ai-payment-badge-panel">
          💳 AI already shared{" "}
          {lead.ai_shared_payment_type === "upi_qr" ? "UPI QR" : lead.ai_shared_payment_type === "razorpay_link" ? "a payment link" : "payment"}
          {lead.ai_shared_payment_amount ? ` of ₹${lead.ai_shared_payment_amount}` : ""}
        </div>
      )}
    </div>

    {/* Payment Link — opens the existing modal to create/view links */}
    <div className="px-4 py-3 border-t border-[--border]" data-testid="payment-link-section">
      <p className="text-[10px] font-bold text-[--text-muted] uppercase tracking-wider mb-2">Payment</p>
      <button
        onClick={onPaymentLinkOpen}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-white text-[11px] font-bold transition-all hover:scale-[1.01] shadow-sm"
        style={{ background: "linear-gradient(135deg, #EA580C, #DC2626)" }}
        data-testid="payment-link-btn-panel"
        title="Create / view payment links"
      >
        <IndianRupee size={13} /> Payment Link
      </button>
    </div>

    {/* Issue Tags Section */}
    <div className="px-4 py-3 border-t border-[--border]" data-testid="issue-tags-section">
      <p className="text-[10px] font-bold text-[--text-muted] uppercase tracking-wider mb-2">Issue Tags</p>
      <div className="flex flex-wrap gap-1.5">
        {availableIssueTags.map((tag) => {
          const active = (lead?.issue_tags || []).includes(tag);
          return (
            <button
              key={tag}
              onClick={() => onToggleIssueTag(tag)}
              className={`px-2 py-0.5 rounded-full text-[10px] font-bold transition-all border ${
                active
                  ? "bg-purple-50 text-purple-700 border-purple-300"
                  : "bg-white text-[--text-muted] border-[--border-dark] hover:border-purple-200"
              }`}
              data-testid={`issue-tag-${tag}`}
            >
              {tag}
            </button>
          );
        })}
      </div>
    </div>
  </>
);

export default AgentToolsPanel;
